import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { Formik, Form } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { parse, format, subDays } from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../../shared/_TBComponents/DatePicker';
import Modal from '../../../../../shared/_TBComponents/Modal';
import Text from '../../../../../shared/_TBComponents/Text';
import Toggle from '../../../../../shared/_TBComponents/Toggle';
import { showSuccessToast, showErrorToast } from '../../../../../shared/utils/toastManager';
import { complexPasswordRegex } from '../../../../../shared/utils/registrationSchemaHelper';

import AddProgramModal from './AddProgramModal';
import AthleteProfileForm from '../forms/AthleteProfileForm';
import AthleteCalendarForm from '../forms/AthleteCalendarForm';
import AthleteProgramForm from '../forms/AthleteProgramForm';
import ContactNestedModal from './ContactNestedModal';
import ConfirmModal from './ConfirmModal';
import {
  assignAthletePrograms,
  createReportcardRecipient,
  deleteAthleteCalendar,
  deleteAthleteProgram,
  deleteReportcardRecipient,
  fetchAthleteAssignments,
  fetchAthleteCalendars,
  fetchAthletePrograms,
  fetchAthletes,
  fetchReportcardRecipients,
  restartAthleteProgram,
  updateReportcardRecipient,
  updateUser,
} from '../../../ducks/navigatorActions';

const FormWrapper = styled.div`
  width: min(850px, 100vw);
  min-width: min(600px, 100vw);
  min-height: min(800px, calc(100vh - 80px));
  max-height: calc(100vh - 150px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;

  @media screen and (max-width: 767px) {
    width: 100vw;
    min-width: unset;
    min-height: calc(100vh - 80px);
    max-height: calc(100vh - 80px);
  }
`;

const FormLayout = styled(Form)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 0 12px 20px;
`;

const TabRow = styled.div`
  display: flex;
  background: #F7F7F7;
  padding-top: 2px;
`;

const TabButton = styled.button`
  flex: 1;
  position: relative;
  border: none;
  background: ${(props) => (props.isActive ? '#FFF' : 'transparent')};
  padding: 8px 16px;
  cursor: pointer;

  @media screen and (max-width: 767px) {
    padding: 8px 4px;
    font-size: 11px;
  }

  /* Border styles for active tabs */
  border-top: ${(props) => (props.isActive ? '2px solid #77A7FF' : 'none')};
  border-left: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-right: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '2px solid #77A7FF' : 'none')};
  border-bottom: ${(props) => (props.isActive ? 'none' : '2px solid #77A7FF')};
  border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
  border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  z-index: ${(props) => (props.isActive ? 2 : 1)};

  &:hover {
    background: #FFF;
    border-top-left-radius: ${(props) => (props.isActive && (props.isLast || props.isMiddle) ? '12px' : '0')};
    border-top-right-radius: ${(props) => (props.isActive && (props.isFirst || props.isMiddle) ? '12px' : '0')};
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;

  @media screen and (max-width: 767px) {
    padding: 16px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  flex-shrink: 0;
  padding: 16px 24px;
  background: white;
  border-top: 1px solid #EEEEEE;

  @media screen and (max-width: 767px) {
    box-sizing: border-box;
    padding: 12px 16px;
  }
`;

const NestedModalContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
  gap: 10px;
`;

const NestedModalFooter = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 8px 0;
`;

const DateOffsetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const EditAthleteModal = ({
  isOpen, onClose, athlete, calendars, allCalendars, programs, groups, initialTab, addProgramMode,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isUpdatingUser = useSelector((state) => state.navigator.ui.isUpdatingSelectedUser);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const athleteAssignments = useSelector((state) => state.navigator.data.selectedUserAssignments);
  const athleteFilters = useSelector((state) => state.navigator.data.athleteFilters);
  const selectedUser = useSelector((state) => state.navigator.data.selectedUser);
  const athleteCalendars = useSelector((state) => state.navigator.data.athleteCalendars);
  const athletePrograms = useSelector((state) => state.navigator.data.athletePrograms);
  const athleteProgramsError = useSelector((state) => state.navigator.data.athleteProgramsError);

  const [activeTab, setActiveTab] = useState('profile');
  const [nestedModalOpen, setNestedModalOpen] = useState(false);
  const [nestedModalType, setNestedModalType] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const formikDirtyRef = useRef(false);
  const tabContainerRef = useRef(null);
  const pendingSubmitRef = useRef(null);

  const handleCloseAttempt = () => {
    if (formikDirtyRef.current) {
      setNestedModalType('unsavedChanges');
      setNestedModalOpen(true);
    } else {
      onClose();
    }
  };

  const [calendarForm, setCalendarForm] = useState({
    calendarId: null,
    assignmentId: null,
    name: '',
    dateStart: '',
    dateEnd: '',
    dateOffset: null,
    active: false,
  });
  const [programForm, setProgramForm] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '' });

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'profile');
    }
  }, [isOpen, initialTab]);

  const toApiDate = (dateStr) => (dateStr
    ? format(parse(dateStr, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd')
    : undefined);

  const refreshContacts = () => {
    dispatch(fetchReportcardRecipients(
      currentUser.accountCode,
      athlete.id,
      {
        onSuccess: (data) => setContacts(
          data.map((r) => ({
            id: r.id,
            name: r.fullName,
            email: r.email,
            status: r.optedOut ? 'Unsubscribed' : 'Subscribed',
          })),
        ),
      },
    ));
  };

  useEffect(() => {
    if (isOpen && currentUser?.accountCode && athlete?.id) {
      batch(() => {
        dispatch(fetchAthleteCalendars(currentUser.accountCode, athlete.id));
        dispatch(fetchAthletePrograms(currentUser.accountCode, athlete.id));
      });
      refreshContacts();
    }
  }, [isOpen, currentUser?.accountCode, athlete?.id]);

  useEffect(() => {
    if (isOpen && addProgramMode) {
      setActiveTab('programs');
      setNestedModalType('addProgram');
      setNestedModalOpen(true);
    }
  }, [isOpen, addProgramMode]);

  const SELECTBOX_STYLES = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '14px',
      fontWeight: 600,
      borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0',
      boxShadow: 'none',
      '&:hover': { borderColor: state.isFocused ? '#77A7FF' : '#D0D0D0' },
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: 'Nunito Sans',
      fontSize: '14px',
      fontWeight: 600,
      backgroundColor: state.isFocused || state.isSelected ? 'rgba(119, 167, 255, 0.15)' : 'transparent',
      color: '#333333',
      '&:active': { backgroundColor: 'rgba(119, 167, 255, 0.25)' },
    }),
  };

  const openNestedModal = (type, contactIndex = null, data = null) => {
    setNestedModalType(type);

    // If editing then populate form with existing data
    if (type === 'editCalendar' && contactIndex !== null) {
      const cal = athleteCalendars[contactIndex] || {};
      const toFormDate = (d) => (d && d !== 'N/A' ? format(parse(d, 'yyyy-MM-dd', new Date()), 'MM/dd/yyyy') : '');
      setCalendarForm({
        calendarId: cal.calendarId || null,
        assignmentId: cal.id || null,
        name: cal.calendarName || '',
        dateStart: toFormDate(cal.dateStart),
        dateEnd: toFormDate(cal.dateEnd),
        dateOffset: (cal.dateOffset && cal.dateOffset !== 'N/A') ? toFormDate(cal.dateOffset) : null,
        active: !!cal.isActiveCalendar,
      });
    } else if (type === 'deleteCalendar' && contactIndex !== null) {
      const cal = athleteCalendars[contactIndex] || {};
      setCalendarForm((prev) => ({
        ...prev,
        calendarId: cal.calendarId || null,
        assignmentId: cal.id || null,
        name: cal.calendarName || '',
      }));
    } else if (type === 'restartProgram' && data !== null) {
      setProgramForm(data);
    } else if (type === 'deleteProgram' && data !== null) {
      setProgramForm(data);
    }
    setContactForm({ name: '', email: '' });
    setEditingContactIndex(null);

    if (type === 'editContact' && contactIndex !== null) {
      setEditingContactIndex(contactIndex);
      setContactForm(contacts[contactIndex]);
    } else if (type === 'deleteContact' && contactIndex !== null) {
      setEditingContactIndex(contactIndex);
    }

    setNestedModalOpen(true);
  };

  const closeNestedModal = () => {
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current.setSubmitting(false);
      pendingSubmitRef.current = null;
    }
    setNestedModalOpen(false);
    setEditingContactIndex(null);
    setContactForm({ name: '', email: '' });
    setCalendarForm({
      calendarId: null,
      assignmentId: null,
      name: '',
      dateStart: '',
      dateEnd: '',
      dateOffset: null,
      active: false,
    });
    setProgramForm(null);
  };

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value,
    });
  };

  // Shared success and error handlers for contact, calendar, and program modal actions
  const contactCallbacks = {
    onSuccess: () => { refreshContacts(); closeNestedModal(); },
    onError: (err) => showErrorToast(err?.response?.headers?.message || 'Failed to save contact. Please try again.'),
  };

  const calendarCallbacks = (successMsg, errorFallback) => ({
    onSuccess: () => {
      showSuccessToast(successMsg);
      batch(() => {
        dispatch(fetchAthleteCalendars(currentUser.accountCode, athlete.id));
        dispatch(fetchAthleteAssignments(currentUser.accountCode, athlete.id));
        dispatch(fetchAthletes(currentUser.accountCode, athleteFilters));
      });
      closeNestedModal();
    },
    onError: (err) => showErrorToast(err?.response?.headers?.message || errorFallback),
  });

  const programCallbacks = (successMsg, errorFallback) => ({
    onSuccess: () => {
      showSuccessToast(successMsg);
      batch(() => {
        dispatch(fetchAthletePrograms(currentUser.accountCode, athlete.id));
        dispatch(fetchAthleteAssignments(currentUser.accountCode, athlete.id));
      });
      closeNestedModal();
    },
    onError: (err) => showErrorToast(err?.response?.headers?.message || errorFallback),
  });

  const handleContactSave = () => {
    const { accountCode } = currentUser;
    const existing = contacts[editingContactIndex];
    const contactData = { fullName: contactForm.name, email: contactForm.email };

    if (nestedModalType === 'addContact') {
      dispatch(createReportcardRecipient(accountCode, athlete.id, contactData, contactCallbacks));
    } else if (nestedModalType === 'editContact') {
      dispatch(updateReportcardRecipient(
        accountCode,
        athlete.id,
        existing.id,
        contactData,
        contactCallbacks,
      ));
    } else if (nestedModalType === 'deleteContact') {
      dispatch(deleteReportcardRecipient(accountCode, athlete.id, existing.id, contactCallbacks));
    }
  };

  const handleCalendarSave = () => {
    const { accountCode } = currentUser;

    if (nestedModalType === 'deleteCalendar') {
      dispatch(deleteAthleteCalendar(
        accountCode,
        athlete.id,
        calendarForm.assignmentId,
        calendarCallbacks(
          'Calendar assignment removed.',
          'Unable to remove calendar assignment. Please try again.',
        ),
      ));
    } else if (nestedModalType === 'editCalendar') {
      dispatch(updateUser(
        accountCode,
        athlete.id,
        {
          groupIds: (athleteAssignments?.groups || []).map((g) => g.groupId),
          user: {
            firstName: athlete.first,
            lastName: athlete.last,
            email: athlete.email,
            mobileNumber: athlete.phone || '',
            countryCode: athlete.countryCode || '',
            gender: athlete.gender ? Number(athlete.gender) : 0,
            ssoId: athlete.ssoId || '',
            externalId: athlete.externalId || '',
            smartabaseId: athlete.smartabaseId || '',
            calendarFutureHide: Number(athlete.calendarFutureHide ?? 0),
          },
          calendar: {
            calendarId: calendarForm.calendarId,
            ...(calendarForm.assignmentId != null && { assignmentId: calendarForm.assignmentId }),
            ...(calendarForm.dateStart && { startDate: toApiDate(calendarForm.dateStart) }),
            ...(calendarForm.dateEnd && { endDate: toApiDate(calendarForm.dateEnd) }),
            ...(calendarForm.dateOffset && { offsetDate: toApiDate(calendarForm.dateOffset) }),
          },
        },
        calendarCallbacks(
          'Calendar assignment updated.',
          'Unable to update calendar assignment. Please try again.',
        ),
      ));
    }
  };

  const handleProgramSave = () => {
    const { accountCode } = currentUser;

    if (nestedModalType === 'restartProgram') {
      dispatch(restartAthleteProgram(
        accountCode,
        athlete.id,
        programForm.dayBasedProgramId,
        programCallbacks(
          `${programForm.programName} has been restarted.`,
          'Unable to restart program. Please try again.',
        ),
      ));
    } else if (nestedModalType === 'deleteProgram') {
      dispatch(deleteAthleteProgram(
        accountCode,
        programForm.dayBasedProgramId,
        athlete.id,
        programCallbacks(
          `${programForm.programName} has been removed.`,
          'Unable to remove program assignment. Please try again.',
        ),
      ));
    }
  };

  const CALENDAR_VISIBILITY_OPTIONS = [
    { id: '0', name: 'Show all future workouts' },
    { id: '1', name: 'Show only 1 week in advance' },
    { id: '2', name: 'Show only up to current day' },
  ];

  // Validation schema
  const validationSchema = Yup.object().shape({
    first: Yup.string().required('First name is required'),
    last: Yup.string().required('Last name is required'),
    email: usernamesEnabled
      ? Yup.string().trim().min(1, 'Username is required').required('Username is required')
      : Yup.string()
        .required('Email address is required')
        .test('email-format-if-changed', 'Please enter a valid email address', (value) => {
          if (value === athlete?.email) return true;
          return Yup.string().email().isValidSync(value);
        }),
    changePassword: Yup.string()
      .test('password-validation', 'Passwords must contain 8 characters, 1 lowercase, 1 uppercase, 1 number, and 1 symbol.', (value) => {
        if (!value || value === '') return true;
        return complexPasswordRegex.test(value);
      }),
    confirmChangePassword: Yup.string().when('changePassword', {
      is: (val) => !!val && val.length > 0,
      then: (schema) => schema
        .required('Please confirm password')
        .oneOf([Yup.ref('changePassword')], 'Passwords do not match'),
      otherwise: (schema) => schema.oneOf([Yup.ref('changePassword'), ''], 'Passwords do not match'),
    }),
    changePIN: Yup.string()
      .test('pin-format', 'PIN must be 4 digits', (value) => {
        if (!value || value === '') return true;
        return /^\d{4}$/.test(value);
      }),
    confirmChangePIN: Yup.string().when('changePIN', {
      is: (val) => !!val && val.length > 0,
      then: (schema) => schema
        .required('Please confirm PIN')
        .oneOf([Yup.ref('changePIN')], 'PINs do not match'),
      otherwise: (schema) => schema.oneOf([Yup.ref('changePIN'), ''], 'PINs do not match'),
    }),
  });

  // Initialize all form values for all tabs
  const initialValues = athlete ? {
    first: athlete.first || '',
    last: athlete.last || '',
    email: (usernamesEnabled ? athlete.username : athlete.email) || '',
    ssoId: athlete.ssoId || '',
    mobileCountry: athlete.countryCode || athlete.phoneCountryCode || '',
    mobileNumber: athlete.phone || '',
    gender: (() => {
      if (!athlete.gender) return null;
      const genderNames = { 1: 'Male', 2: 'Female', 3: 'Other' };
      return { id: String(athlete.gender), name: genderNames[athlete.gender] || 'Other' };
    })(),
    externalId: athlete.externalId || '',
    changePassword: '',
    confirmChangePassword: '',
    changePIN: '',
    confirmChangePIN: '',
    smartabaseId: athlete.smartabaseId || '',
    groups: (athleteAssignments?.groups || [])
      .filter((g) => groups.some((accessible) => accessible.id === g.groupId))
      .map((g) => ({ id: g.groupId, name: g.groupName })),
    calendarAssignment: null,
    calendarVisibility: (() => {
      const hideValue = athlete.calendar_future_hide ?? athlete.calendarFutureHide;
      if (hideValue === 0) return CALENDAR_VISIBILITY_OPTIONS[0];
      if (hideValue === 1) return CALENDAR_VISIBILITY_OPTIONS[1];
      if (hideValue === 2) return CALENDAR_VISIBILITY_OPTIONS[2];
      return CALENDAR_VISIBILITY_OPTIONS[0];
    })(),
    startDate: '',
    endDate: '',
    dateOffset: null,
  } : {};

  if (!athlete) return null;

  // Filter out groups on restricted coach to prevent them being removed but include in payload
  const lockedGroups = (athleteAssignments?.groups || [])
    .filter((g) => !groups.some((accessible) => accessible.id === g.groupId))
    .map((g) => ({ id: g.groupId, name: g.groupName }));

  const buildPayload = (values) => {
    const mergedValues = {
      ...values,
      groups: [...(values.groups || []), ...lockedGroups],
    };
    if (usernamesEnabled) {
      const { email, ...rest } = mergedValues;
      return { ...rest, username: email };
    }
    return mergedValues;
  };

  const dispatchUpdateUser = (values, setSubmitting, afterSuccess) => {
    const payload = buildPayload(values);

    dispatch(updateUser(
      currentUser.accountCode,
      athlete.id,
      {
        groupIds: (payload.groups || []).map((g) => g.id),
        user: {
          firstName: payload.first,
          lastName: payload.last,
          ...(payload.username ? { username: payload.username } : { email: payload.email }),
          mobileNumber: payload.mobileNumber !== '' && payload.mobileNumber != null ? String(payload.mobileNumber) : '',
          countryCode: payload.mobileCountry || '',
          gender: payload.gender ? Number(payload.gender.id) : 0,
          ssoId: payload.ssoId || '',
          externalId: payload.externalId || '',
          smartabaseId: payload.smartabaseId || '',
          ...(payload.changePassword && { password: payload.changePassword }),
          ...(payload.changePIN && { pin: payload.changePIN }),
          ...(payload.calendarVisibility != null && {
            calendarFutureHide: Number(payload.calendarVisibility.id),
          }),
        },
        ...(payload.calendarAssignment?.id != null && {
          calendar: {
            calendarId: payload.calendarAssignment.id,
            ...(payload.assignmentId != null && { assignmentId: payload.assignmentId }),
            ...(payload.startDate && { startDate: toApiDate(payload.startDate) }),
            ...(payload.endDate && { endDate: toApiDate(payload.endDate) }),
            ...(payload.dateOffset && { offsetDate: toApiDate(payload.dateOffset) }),
          },
        }),
      },
      {
        onSuccess: () => {
          showSuccessToast(`${values.first} ${values.last} has been updated.`);
          setSubmitting(false);
          batch(() => {
            dispatch(fetchAthleteAssignments(currentUser.accountCode, athlete.id));
            dispatch(fetchAthletes(currentUser.accountCode, athleteFilters));
          });
          if (afterSuccess) {
            afterSuccess();
          } else {
            onClose();
          }
        },
        onError: (err) => {
          showErrorToast(err?.response?.headers?.message || 'Unable to update athlete. Please try again.');
          setSubmitting(false);
        },
      },
    ));
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    const coachHasGroupRestrictions = !currentUser?.primaryAdmin && !currentUser?.fullUserAccess;
    const athleteHadAccessibleGroups = (athleteAssignments?.groups || [])
      .some((g) => groups.some((accessible) => accessible.id === g.groupId));

    const athleteWillBeHidden = coachHasGroupRestrictions
      && athleteHadAccessibleGroups
      && values.groups.length === 0;

    if (athleteWillBeHidden) {
      pendingSubmitRef.current = { values, setSubmitting };
      setNestedModalType('confirmGroupRemoval');
      setNestedModalOpen(true);
      return;
    }

    dispatchUpdateUser(values, setSubmitting);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={handleCloseAttempt}
        customBorderRadius='8px'
        customPadding='0px'
        customMaxWidth='850px'
        customMinWidth='min(600px, 100vw)'
        customButton
        widthFitContent
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {(formikProps) => {
            const {
              isSubmitting, values, errors, touched, handleChange, handleBlur, setFieldValue,
              dirty,
            } = formikProps;
            formikDirtyRef.current = dirty;

            return (
              <FormWrapper>
                <FormLayout autoComplete='off' data-1p-ignore='true' data-lpignore='true'>
                  <Header>
                    <Text
                      styledTextProps={{ fontSize: '24px', fontWeight: 700, color: '#1A1A1A' }}
                    >
                      {`${athlete.first} ${athlete.last}`}
                    </Text>
                    <Button
                      icon='remove'
                      iconSize={15}
                      onClick={(e) => { e.preventDefault(); handleCloseAttempt(); }}
                      fontSize='13px'
                      txtColor='#9E9E9E'
                      noBorder
                    />
                  </Header>

                  <TabRow>
                    <TabButton
                      type='button'
                      isFirst
                      isActive={activeTab === 'profile'}
                      onClick={() => setActiveTab('profile')}
                    >
                      <Text styledTextProps={{ color: '#1A1A1A' }}>Profile Info</Text>
                    </TabButton>
                    <TabButton
                      type='button'
                      isMiddle
                      isActive={activeTab === 'calendars'}
                      onClick={() => setActiveTab('calendars')}
                    >
                      <Text styledTextProps={{ color: '#1A1A1A' }}>Calendar Assignments</Text>
                    </TabButton>
                    <TabButton
                      type='button'
                      isLast
                      isActive={activeTab === 'programs'}
                      onClick={() => setActiveTab('programs')}
                    >
                      <Text styledTextProps={{ color: '#1A1A1A' }}>Program Access</Text>
                    </TabButton>
                  </TabRow>

                  <TabContainer ref={tabContainerRef}>
                    {activeTab === 'profile' && (
                      <AthleteProfileForm
                        athlete={athlete}
                        contacts={contacts}
                        groups={groups}
                        selectBoxStyles={SELECTBOX_STYLES}
                        onOpenNestedModal={openNestedModal}
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        setFieldValue={setFieldValue}
                        isSubmitting={isSubmitting}
                        usernamesEnabled={usernamesEnabled}
                        scrollContainerRef={tabContainerRef}
                      />
                    )}
                    {activeTab === 'calendars' && (
                      <AthleteCalendarForm
                        athleteCalendars={athleteCalendars}
                        calendars={calendars}
                        allCalendars={allCalendars}
                        calendarVisibilityOptions={CALENDAR_VISIBILITY_OPTIONS}
                        values={values}
                        setFieldValue={setFieldValue}
                        onOpenNestedModal={openNestedModal}
                        selectBoxStyles={SELECTBOX_STYLES}
                      />
                    )}
                    {activeTab === 'programs' && (
                      <AthleteProgramForm
                        athletePrograms={athletePrograms}
                        programs={programs}
                        onOpenNestedModal={openNestedModal}
                        fetchError={athleteProgramsError}
                        canAccessDayBasedPrograms={selectedUser?.canAccessDayBasedPrograms}
                      />
                    )}
                  </TabContainer>

                  <ButtonContainer>
                    <Button
                      type='submit'
                      name={isUpdatingUser ? 'Saving...' : 'Save Changes'}
                      fontSize='14px'
                      txtColor='#FFF'
                      bgColor='#1A1A1A'
                      borderColor='#1A1A1A'
                      customBorderRadius='6px'
                      styledTextProps={{ fontWeight: 600 }}
                      disabled={isSubmitting || isUpdatingUser}
                    />
                  </ButtonContainer>
                </FormLayout>
              </FormWrapper>
            );
          }}
        </Formik>
      </Modal>

      {/* Nested modal for the one confirm branch that is a real form (has date/toggle inputs) */}
      <Modal
        isOpen={nestedModalOpen && nestedModalType === 'editCalendar'}
        onRequestClose={closeNestedModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
        overlayBackgroundColor='rgba(0, 0, 0, 0.75)'
      >
        <NestedModalContent>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Update Assignment
          </Text>
          <Text styledWrapperProps={{ marginBottom: '16px' }}>
            {`You are editing the ${calendarForm.name} assignment. Leave the end date blank if you want it to be ongoing.`}
          </Text>

          <DatePicker
            singleDate={false}
            startDate={calendarForm.dateStart ? parse(calendarForm.dateStart, 'MM/dd/yyyy', new Date()) : null}
            endDate={calendarForm.dateEnd ? parse(calendarForm.dateEnd, 'MM/dd/yyyy', new Date()) : null}
            setStartDate={(date) => {
              const newForm = { ...calendarForm, dateStart: date ? format(date, 'MM/dd/yyyy') : '' };
              if (date && calendarForm.dateOffset) {
                const offset = parse(calendarForm.dateOffset, 'MM/dd/yyyy', new Date());
                if (offset >= date) newForm.dateOffset = '';
              }
              setCalendarForm(newForm);
            }}
            setEndDate={(date) => setCalendarForm({ ...calendarForm, dateEnd: date ? format(date, 'MM/dd/yyyy') : '' })}
            maxDate={null}
            styledWrapperProps={{ width: 'fit-content', marginBottom: '12px' }}
          />
          <DateOffsetContainer>
            <ToggleContainer>
              <Toggle
                defaultChecked={Boolean(calendarForm.dateOffset)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setCalendarForm({ ...calendarForm, dateOffset: '' });
                  } else {
                    setCalendarForm({ ...calendarForm, dateOffset: null });
                  }
                }}
                onHoverColor='#0DCC8A'
                onColor='#0DCC8A'
                offColor='#E0E0E0'
                thumbColorOff
                thumbBorderOff
                width='auto'
              />
              <Text styledTextProps={{ fontSize: '14px', fontWeight: 600, color: '#333333' }}>
                Enable Calendar Offset
                <br />
                (Set Specific Date as First Workout)
              </Text>
            </ToggleContainer>
            {calendarForm.dateOffset !== null && (
            <DatePicker
              singleDate
              startDate={calendarForm.dateOffset ? parse(calendarForm.dateOffset, 'MM/dd/yyyy', new Date()) : null}
              setStartDate={(date) => setCalendarForm({ ...calendarForm, dateOffset: date ? format(date, 'MM/dd/yyyy') : '' })}
              maxDate={calendarForm.dateStart ? subDays(parse(calendarForm.dateStart, 'MM/dd/yyyy', new Date()), 1) : null}
              styledWrapperProps={{ width: 'fit-content' }}
              placeholderText='Select offset date'
            />
            )}
          </DateOffsetContainer>
          <NestedModalFooter>
            <Button
              name='Cancel'
              fontSize='14px'
              txtColor='#757575'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={(e) => { e.preventDefault(); closeNestedModal(); }}
            />
            <Button
              name='Save'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={(e) => { e.preventDefault(); handleCalendarSave(); }}
            />
          </NestedModalFooter>
        </NestedModalContent>
      </Modal>

      <ConfirmModal
        isOpen={nestedModalOpen && nestedModalType === 'deleteCalendar'}
        onClose={closeNestedModal}
        onConfirm={handleCalendarSave}
        title='Delete Calendar Assignment'
        message={`Are you sure you want to delete this ${calendarForm.name} assignment?`}
      />

      <ConfirmModal
        isOpen={nestedModalOpen && nestedModalType === 'restartProgram'}
        onClose={closeNestedModal}
        onConfirm={handleProgramSave}
        title='Restart Program'
        message={`Are you sure you want to restart ${programForm?.programName}?`}
        confirmLabel='Restart'
        confirmIcon='restart'
        destructive={false}
      />

      <ConfirmModal
        isOpen={nestedModalOpen && nestedModalType === 'deleteProgram'}
        onClose={closeNestedModal}
        onConfirm={handleProgramSave}
        title='Delete Program Assignment'
        message={`Are you sure you want to delete this ${programForm?.programName} assignment?`}
      />

      <ConfirmModal
        isOpen={nestedModalOpen && nestedModalType === 'unsavedChanges'}
        onClose={closeNestedModal}
        onConfirm={() => { closeNestedModal(); onClose(); }}
        title='Unsaved Changes'
        message='You have unsaved changes. Are you sure you want to leave?'
        confirmLabel='Leave'
        confirmIcon={null}
      />

      <ConfirmModal
        isOpen={nestedModalOpen && nestedModalType === 'confirmGroupRemoval'}
        onClose={closeNestedModal}
        onConfirm={() => {
          const { values, setSubmitting } = pendingSubmitRef.current;
          pendingSubmitRef.current = null;
          closeNestedModal();
          dispatchUpdateUser(values, setSubmitting, () => { onClose(); history.push('/dashboard/athletes'); });
        }}
        title='Remove Group Access'
        message={`You are removing all groups you have access to from ${athlete?.first} ${athlete?.last}. Due to your account's group restrictions, this athlete will no longer appear in your athlete list after saving. Are you sure you want to continue?`}
        confirmLabel='Save'
        confirmIcon={null}
        destructive={false}
      />

      <ContactNestedModal
        isOpen={nestedModalOpen}
        onClose={closeNestedModal}
        type={nestedModalType}
        contactForm={contactForm}
        onContactFormChange={handleContactFormChange}
        onSave={handleContactSave}
      />

      <AddProgramModal
        isOpen={nestedModalOpen && nestedModalType === 'addProgram'}
        onClose={closeNestedModal}
        programs={programs}
        athletePrograms={athletePrograms}
        onSave={(programIds) => dispatch(assignAthletePrograms(
          currentUser.accountCode,
          athlete.id,
          programIds,
          {
            onSuccess: () => {
              showSuccessToast('Programs assigned successfully.');
              batch(() => {
                dispatch(fetchAthletePrograms(currentUser.accountCode, athlete.id));
                dispatch(fetchAthleteAssignments(currentUser.accountCode, athlete.id));
              });
              closeNestedModal();
            },
            onError: (err) => {
              showErrorToast(err?.response?.headers?.message || 'Unable to assign programs. Please try again.');
            },
          },
        ))}
      />
    </>
  );
};

EditAthleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialTab: PropTypes.oneOf(['profile', 'calendars', 'programs']),
  addProgramMode: PropTypes.bool,
  athlete: PropTypes.shape({
    id: PropTypes.number,
    first: PropTypes.string,
    last: PropTypes.string,
    pic: PropTypes.string,
    picture: PropTypes.string,
    email: PropTypes.string,
    username: PropTypes.string,
    ssoId: PropTypes.string,
    countryCode: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    phone: PropTypes.string,
    gender: PropTypes.number,
    externalId: PropTypes.string,
    smartabaseId: PropTypes.string,
    calendarFutureHide: PropTypes.number,
    calendar_future_hide: PropTypes.number,
    groups: PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })),
    ]),
    currentCal: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    calendars: PropTypes.arrayOf(PropTypes.shape({
      calId: PropTypes.number,
      name: PropTypes.string,
      dateStart: PropTypes.string,
      dateEnd: PropTypes.string,
      dateOffset: PropTypes.string,
    })),
    programs: PropTypes.arrayOf(PropTypes.shape({
      dayBasedProgramId: PropTypes.number,
      programName: PropTypes.string,
    })),
    contacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
        status: PropTypes.string,
      }),
    ),
  }),
  calendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  allCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    archived: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  })),
  programs: PropTypes.arrayOf(PropTypes.shape({
    dayBasedProgramId: PropTypes.number,
    programName: PropTypes.string,
  })),
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
};

EditAthleteModal.defaultProps = {
  initialTab: 'profile',
  addProgramMode: false,
  athlete: null,
  calendars: [],
  allCalendars: [],
  programs: [],
  groups: [],
};

export default EditAthleteModal;
